import { useEffect, useRef, useState, useCallback } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../features/auth/auth-store';

interface NavItem {
  label: string;
  to: string;
}

interface NavGroup {
  heading: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    heading: 'Content',
    items: [
      { label: 'Alphabet', to: '/abc' },
      { label: 'Audio Course', to: '/audio-course' },
      { label: 'Chunks', to: '/chunks' },
      { label: 'Dialogs', to: '/dialogs' },
      { label: 'Dramas', to: '/dramas' },
      { label: 'Images', to: '/images' },
      { label: 'Drills', to: '/drills' },
      { label: 'News', to: '/news' },
      { label: 'Stories', to: '/stories' },
    ],
  },
  {
    heading: 'Reference',
    items: [
      { label: 'CEFR All', to: '/cefr' },
      { label: 'Course Matrix', to: '/course-matrix' },
      { label: 'Lexicon', to: '/lexicon' },
      { label: 'Linguistics', to: '/linguistics' },
      { label: 'Metagraph', to: '/metagraph' },
      { label: 'N+1', to: '/n1' },
      { label: 'Numbers', to: '/numbers' },
      { label: 'Paremiology', to: '/paremiology' },
      { label: 'Phonics', to: '/phonics' },
      { label: 'Phrases', to: '/phrases' },
      { label: 'Regex', to: '/regex' },
      { label: 'Topics', to: '/topics' },
      { label: 'TW Readings', to: '/tw-readings' },
    ],
  },
  {
    heading: 'Operations',
    items: [
      { label: 'Agents', to: '/agents' },
      { label: 'Books', to: '/books' },
      { label: 'Campaigns', to: '/campaigns' },
      { label: 'Coverage', to: '/coverage' },
      { label: 'Emails', to: '/emails' },
      { label: 'Languages', to: '/languages' },
      { label: 'Financials', to: '/financials' },
      { label: 'Atlas', to: '/atlas' },
      { label: 'Publishing', to: '/publishing' },
      { label: 'QA', to: '/qa' },
      { label: 'Transcription', to: '/transcription' },
      { label: 'Users', to: '/users' },
      { label: 'Viva', to: '/viva' },
    ],
  },
];

function navLinkClass({ isActive }: { isActive: boolean }): string {
  const base =
    'block px-3 py-2 md:py-1.5 rounded text-sm transition-colors duration-150';
  return isActive
    ? `${base} bg-accent-teal/15 text-accent-teal font-medium`
    : `${base} text-gray-400 hover:text-gray-200 hover:bg-white/5`;
}

export default function Layout() {
  const { user, signOut } = useAuthStore();
  const mainRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const sidebarContent = (
    <>
      <div className="p-5 border-b border-white/5 flex items-center justify-between">
        <NavLink to="/" className="block" onClick={closeMenu}>
          <h2 className="font-heading text-xl font-bold text-gray-100">
            Glossika Content
          </h2>
          <p className="font-mono text-xs text-gray-500 mt-1">
            mazmun.glossika.com
          </p>
        </NavLink>
        <button
          onClick={closeMenu}
          className="md:hidden p-1 text-gray-400 hover:text-gray-200"
          aria-label="Close menu"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 5l10 10M15 5L5 15" />
          </svg>
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
        {navGroups.map((group) => (
          <div key={group.heading}>
            <h3 className="px-3 mb-2 text-[11px] font-body font-medium uppercase tracking-wider text-gray-500">
              {group.heading}
            </h3>
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink to={item.to} className={navLinkClass} onClick={closeMenu}>
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="p-4 border-t border-white/5 space-y-2">
        {user && (
          <p className="font-mono text-xs text-gray-500 truncate">{user.email}</p>
        )}
        <button
          onClick={() => { signOut(); closeMenu(); }}
          className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
        >
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 shrink-0 bg-bg-secondary border-r border-white/5 flex-col">
        {sidebarContent}
      </aside>

      {/* Mobile overlay + drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 md:hidden" onClick={closeMenu} />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-bg-secondary border-r border-white/5 flex flex-col transform transition-transform duration-200 ease-in-out md:hidden ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {sidebarContent}
      </aside>

      {/* Main content */}
      <main ref={mainRef} className="flex-1 overflow-y-auto min-w-0">
        {/* Mobile top bar */}
        <div className="sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-bg-primary/95 backdrop-blur border-b border-white/5 md:hidden">
          <button
            onClick={() => setMenuOpen(true)}
            className="p-1 text-gray-400 hover:text-gray-200"
            aria-label="Open menu"
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6h14M4 11h14M4 16h14" />
            </svg>
          </button>
          <span className="font-heading text-sm font-bold text-gray-200 truncate">Glossika Content</span>
        </div>

        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
